/**
 * Altura de um DataTable — os DOIS contratos suportados, e só eles.
 *
 * 1. MEDIDA (`heightMode: 'fill-viewport'`, o padrão): a tabela lê onde está e
 *    ocupa até o fim da janela, nunca menos que 75% dela — mesmo vazia ou com
 *    poucas linhas (a área não encolhe até o conteúdo). Nenhuma página precisa
 *    saber quanto chrome tem acima dela.
 * 2. DETERMINÍSTICA (`heightMode: 'fixed'` + `height`): um valor literal em px,
 *    o mesmo em qualquer tela. Não aceita `vh`, `calc()` nem `%`.
 *
 * O que ficou de fora é a zona do meio que produziu o chamado 2026-0041:
 * `calc(100vh - N)` escrito à mão, com N acertado para UMA página e errado para
 * a vizinha — /despesas e /fechamentos usam os mesmos 300px; na primeira a
 * tabela preenche, na segunda começa em y=1522 numa janela de 768.
 *
 * Funções puras, sem DOM: o DataTable mede, estas decidem. É o que permite
 * testar a aritmética num tier que não faz layout.
 */

/** Piso do modo medido: a tabela nunca fica com menos que esta fração da janela (chamado 2026-0078; 50% → 75% em 2026-10-14, pedido do Ivan). */
export const FILL_VIEWPORT_MIN_RATIO = 0.75;
export const FIXED_DEFAULT_PX = 500;

/** Os modos aceitos. `fill-screen` é o nome antigo de `fill-viewport`. */
export const HEIGHT_MODES = ['fill-viewport', 'fixed', 'fill-parent', 'auto', 'fill-screen'] as const;

export interface FillViewportMeasure {
  /** `window.innerHeight`. */
  viewportHeight: number;
  /** Topo do corpo rolável da tabela, na coordenada do DOCUMENTO (rect.top + scrollY). */
  bodyTopInDocument: number;
  /** O que a própria tabela desenha abaixo do corpo: paginação, linha de totais. */
  belowBody: number;
  /** O respiro do layout abaixo da página: padding do .app-main + rodapé da versão. */
  layoutGutter: number;
}

/**
 * Quanto sobra da janela para o corpo da tabela.
 *
 * O piso é três quartos da janela: uma tabela que começa abaixo da dobra (/pdv/entregas
 * em y=535, /clientes em y=1125) não tem espaço nenhum — e zero não é uma altura.
 * Nesses casos a PÁGINA rola e a tabela ainda tem ao menos 75% da janela.
 */
export function availableBodyHeight(m: FillViewportMeasure): number {
  const disponivel = Math.floor(m.viewportHeight - m.bodyTopInDocument - m.belowBody - m.layoutGutter);

  return Math.max(Math.floor(m.viewportHeight * FILL_VIEWPORT_MIN_RATIO), disponivel);
}

/**
 * `height` de um modo `fixed` → px, ou `null` quando o valor não é determinístico.
 *
 * Aceita número (px) e `'NNNpx'`. Recusa tudo o que depende da janela ou do pai
 * (`vh`, `calc()`, `%`, `rem` também — o que varia com a fonte do usuário não é
 * um valor fixo).
 */
export function fixedHeightPx(height: unknown): string | null {
  if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
    return `${height}px`;
  }

  if (typeof height === 'string' && /^\d+(\.\d+)?px$/.test(height.trim())) {
    return height.trim();
  }

  return null;
}

export interface ResolveInput {
  mode: string | undefined;
  height: unknown;
  /** Resultado da última medição; `null` antes da primeira. */
  measuredPx: number | null;
  /** A tabela está dentro de um diálogo — a janela não é o limite dela. */
  insideDialog: boolean;
}

export interface ResolveOutput {
  /** Valor para o `scrollHeight` do PrimeVue ('' = sem teto; 'flex' = ocupa o pai). */
  scrollHeight: string;
  /** Se o DataTable deve medir a si mesmo. */
  measures: boolean;
  /**
   * O corpo deve ter EXATAMENTE `scrollHeight` (height + min-height), não só
   * o teto — verdadeiro apenas no modo medido fora de diálogo.
   */
  fillsBody: boolean;
  /** Configuração recusada — para o log, nunca para o usuário. */
  problem: string | null;
}

/**
 * Decide o `scrollHeight` do PrimeVue. Um px vira MAX-height no PrimeVue 4.4:
 * lista curta continua curta, lista longa cresce até o teto e rola por dentro.
 * Por isso, no modo medido (`fillsBody`), o DataTable aplica também o mesmo
 * valor como height/min-height no corpo — vazia ou com poucas linhas, a área
 * não encolhe até o conteúdo.
 */
export function resolveScrollHeight(input: ResolveInput): ResolveOutput {
  const mode = input.mode ?? 'fill-viewport';

  if (mode === 'fixed') {
    const px = fixedHeightPx(input.height);
    if (px !== null) {
      return { scrollHeight: px, measures: false, fillsBody: false, problem: null };
    }

    return {
      scrollHeight: `${FIXED_DEFAULT_PX}px`,
      measures: false,
      fillsBody: false,
      problem: input.height === undefined
        ? null
        : `heightMode 'fixed' exige um valor em px; recebeu ${JSON.stringify(input.height)} — usando ${FIXED_DEFAULT_PX}px`,
    };
  }

  if (mode === 'fill-parent') {
    return { scrollHeight: 'flex', measures: false, fillsBody: false, problem: null };
  }

  if (mode === 'auto') {
    // Sem teto: a altura é a do conteúdo. É o que o tipo sempre documentou, e o
    // que o `switch` antigo não fazia (caía no 100vh - 400px do padrão).
    return { scrollHeight: '', measures: false, fillsBody: false, problem: null };
  }

  const conhecido = (HEIGHT_MODES as readonly string[]).includes(mode);
  const ignoradoHeight = input.height !== undefined
    ? `\`height\` é ignorado em '${mode}' — a altura é medida; para um valor fixo use heightMode 'fixed'`
    : null;
  const problem = !conhecido ? `heightMode desconhecido '${mode}' — tratado como 'fill-viewport'` : ignoradoHeight;

  // Num diálogo a janela não é o limite (o diálogo tem altura e rolagem próprias):
  // medir contra a janela faria o diálogo crescer até o fim da tela.
  if (input.insideDialog) {
    return { scrollHeight: `${FIXED_DEFAULT_PX}px`, measures: false, fillsBody: false, problem };
  }

  return {
    scrollHeight: input.measuredPx !== null ? `${input.measuredPx}px` : `${FIXED_DEFAULT_PX}px`,
    measures: true,
    fillsBody: input.measuredPx !== null,
    problem,
  };
}
